import React, { memo, useCallback, useMemo, useState } from 'react';
import { RefreshControl, View } from 'react-native';
import { useNavigation } from '@react-navigation/native';
import { Feather } from '@expo/vector-icons';

import { useUserContext } from '../../../common/utils/context';
import { MY_DAY_STEP_STATUS } from '../../../common/utils/myDay/driverDaySteps';
import { getLocalTime } from '../../../common/utils/time/time';
import Text from '../../components/text/Text';
import {
	ActionDock,
	Card,
	EmptyState,
	PrimaryButton,
	ProgressBar,
	Screen,
	Sheet,
	StepRow,
	STEP_STATUS,
} from '../../design/components';
import { useRefreshPage } from '../../hooks';
import { useThemedStyles } from '../../hooks/useThemedStyles';
import { useTheme } from '../../theme';
import DriverClockBadge from './DriverClockBadge';
import { useDriverDay } from './DriverDayProvider';
import DriverTrainingBanner from './DriverTrainingBanner';
import { DRIVER_ROUTE, RUN_LOAD_ERROR } from './driverRoutes';
import { END_OF_DAY_ROUTE } from './endOfDay/endOfDayRoutes';
import { countLabel } from './endOfDay/stopLabels';
import useEndOfDaySteps, { RETURN_KEYS_STEP_KEY } from './endOfDay/useEndOfDaySteps';
import {
	getOptionalStepTags,
	getPhaseStepStatus,
	getRequiredStepProgress,
	getSignOffStepStatus,
} from './stepStatus';
import { STEP_SYNC_STATE } from './stepSyncState';
import useDriverDayActions from './useDriverDayActions';

const STEP_ROUTE = {
	moneyBags: END_OF_DAY_ROUTE.MONEY_BAGS,
	[RETURN_KEYS_STEP_KEY]: END_OF_DAY_ROUTE.RETURN_KEYS,
	returnStock: END_OF_DAY_ROUTE.RETURN_STOCK,
};

const ACTION_LABEL = {
	returnedToWarehouse: 'Confirm returned to warehouse',
	moneyBags: 'Return money bags',
	[RETURN_KEYS_STEP_KEY]: 'Return keys and passes',
	returnStock: 'Return stock',
	vehicleChecklist: 'Open vehicle checklist',
	selfDebrief: 'Open debrief',
	clockOut: 'Open shift pass',
};

const SYNC_STATUS_TEXT = {
	[STEP_SYNC_STATE.PENDING]: { text: 'Saved on phone', tone: 'secondary' },
	[STEP_SYNC_STATE.FAILED]: { text: 'Sync failed', tone: 'warning' },
};

const EndStepRow = memo(({ step, status, subtitle, subtitleTone, onPressStep }) => {
	const handlePress = useCallback(() => onPressStep(step), [onPressStep, step]);
	const syncStatus = SYNC_STATUS_TEXT[step.syncState];
	const signOffStatus = status === STEP_STATUS.DONE ? getSignOffStepStatus(step) : undefined;
	// Returned to warehouse is a one-off confirmation; once recorded there is nothing to open.
	const isPressable = !(step.key === 'returnedToWarehouse' && status === STEP_STATUS.DONE);

	return (
		<StepRow
			status={status}
			title={step.label}
			subtitle={subtitle}
			subtitleTone={subtitleTone}
			statusText={syncStatus?.text || signOffStatus?.statusText || (status === STEP_STATUS.DONE ? 'Done' : undefined)}
			statusTone={syncStatus?.tone || signOffStatus?.statusTone}
			tags={getOptionalStepTags(step)}
			onPress={isPressable ? handlePress : undefined}
		/>
	);
});

/**
 * End of day (SPEC section 3): run summary, the end-of-day steps with the next
 * one highlighted, and one primary button that follows it. Steps with inputs
 * open their own screens; Returned to warehouse confirms in a sheet. Clock out
 * is never locked (decision 10): it warns about unfinished steps instead.
 */
const EndOfDayView = () => {
	const navigation = useNavigation();
	const styles = useThemedStyles(createStyles);
	const { theme: { ui: { colors } } } = useTheme();
	const { tenant } = useUserContext();
	const day = useDriverDay();
	const {
		serviceRun,
		sortedDockets,
		completedStopsCount,
		allStopsComplete,
		couldNotCompleteDockets,
		activePhaseKey,
		isOnline,
	} = day;
	const {
		openClockPass,
		openStepChecklist,
		confirmReturnedToWarehouse,
		isConfirmingReturnedToWarehouse,
	} = useDriverDayActions(day);
	const { steps, currentKey, getStepAfter, getStepPosition, clockOutWarning } = useEndOfDaySteps(day);
	const [refreshPage, isRefreshing] = useRefreshPage(day.refresh);
	const [isReturnSheetOpen, setIsReturnSheetOpen] = useState(false);

	const openStep = useCallback((step) => {
		switch (step.key) {
			case 'returnedToWarehouse':
				setIsReturnSheetOpen(true);
				break;
			case 'clockOut':
				openClockPass();
				break;
			case 'vehicleChecklist':
			case 'selfDebrief':
				openStepChecklist(step.key, getStepPosition(step.key));
				break;
			default:
				if (STEP_ROUTE[step.key]) {
					navigation.push(STEP_ROUTE[step.key]);
				}
		}
	}, [getStepPosition, navigation, openClockPass, openStepChecklist]);

	const closeReturnSheet = useCallback(() => setIsReturnSheetOpen(false), []);
	const confirmReturnFromSheet = useCallback(async () => {
		await confirmReturnedToWarehouse();
		setIsReturnSheetOpen(false);
	}, [confirmReturnedToWarehouse]);

	const ticketsResolvedCount = useMemo(() => sortedDockets
		.filter(docket => docket.ticketsResolved)
		.reduce((sum, docket) => sum + (Number(docket.driverTicketCount) || 0), 0), [sortedDockets]);

	const currentStep = steps.find(step => step.key === currentKey) || null;
	const requiredProgress = getRequiredStepProgress(steps);
	const returnStep = steps.find(step => step.key === 'returnedToWarehouse') || null;

	const renderDock = () => {
		if (!serviceRun || !day.isEnabled) {
			return null;
		}

		if (currentStep) {
			const stepAfter = getStepAfter(currentStep.key);
			return (
				<ActionDock
					hint={stepAfter ? `After this: ${stepAfter.label}` : undefined}
					action={{ label: ACTION_LABEL[currentStep.key], onPress: () => openStep(currentStep) }}
				/>
			);
		}

		if (activePhaseKey === 'start') {
			return (
				<ActionDock
					hint={`Still open on Start of day: ${day.nextAction.step.label}`}
					action={{ label: 'Finish start of day', onPress: () => navigation.navigate(DRIVER_ROUTE.START_OF_DAY) }}
				/>
			);
		}

		if (activePhaseKey === 'stops') {
			return (
				<ActionDock
					hint={`${completedStopsCount} of ${countLabel(sortedDockets.length, 'stop', 'stops')} done`}
					action={{ label: 'Go to On the run', onPress: () => navigation.navigate(DRIVER_ROUTE.RUN_LIST) }}
				/>
			);
		}

		return null;
	};

	const renderRunCard = () => {
		if (!sortedDockets.length) {
			return null;
		}

		if (allStopsComplete) {
			const summary = [
				`${completedStopsCount} of ${countLabel(sortedDockets.length, 'machine', 'machines')}`,
				ticketsResolvedCount > 0 ? `${countLabel(ticketsResolvedCount, 'ticket', 'tickets')} handled` : null,
			].filter(Boolean).join(' · ');
			return (
				<Card tone="success" style={styles.runCard}>
					<Feather name="check-circle" size={24} color={colors.success} />
					<View style={styles.runCardText}>
						<Text style={styles.runCardTitle}>Run complete</Text>
						<Text style={styles.runCardSummary}>{summary}</Text>
					</View>
				</Card>
			);
		}

		return (
			<Card style={styles.runCard}>
				<Feather name="truck" size={24} color={colors.iconMuted} />
				<View style={styles.runCardText}>
					<Text style={styles.runCardTitle}>Run not finished</Text>
					<Text style={styles.runCardPending}>
						{[
							`${completedStopsCount} of ${countLabel(sortedDockets.length, 'stop', 'stops')} done`,
							couldNotCompleteDockets.length ? `${couldNotCompleteDockets.length} couldn't complete` : null,
						].filter(Boolean).join(' · ')}
					</Text>
				</View>
			</Card>
		);
	};

	const renderBody = () => {
		if (!serviceRun || !day.isEnabled) {
			return <EmptyState title="No run today" message="See your manager if you are expecting a run." />;
		}

		return (
			<>
				{renderRunCard()}
				{requiredProgress.totalCount > 0 && (
					<ProgressBar
						done={requiredProgress.doneCount}
						total={requiredProgress.totalCount}
						label={requiredProgress.label}
					/>
				)}
				<View style={styles.steps}>
					{steps.map((step) => {
						const isClockOutWarning = step.key === 'clockOut' &&
							step.status === MY_DAY_STEP_STATUS.TODO &&
							Boolean(clockOutWarning);
						return (
							<EndStepRow
								key={step.key}
								step={step}
								status={getPhaseStepStatus(step, currentKey)}
								subtitle={isClockOutWarning ? clockOutWarning : step.description}
								subtitleTone={isClockOutWarning ? 'warning' : 'default'}
								onPressStep={openStep}
							/>
						);
					})}
				</View>
			</>
		);
	};

	return (
		<Screen
			backLabel="Home"
			headerRight={<DriverClockBadge />}
			heading="End of day"
			subheading={serviceRun
				? [
					serviceRun.route?.name || 'Routeless service run',
					getLocalTime(tenant?.timezone, serviceRun.planned).format('ddd D MMM'),
				].join(' · ')
				: undefined}
			isLoading={day.isLoading}
			error={day.loadError ? RUN_LOAD_ERROR : undefined}
			onRetry={day.refresh}
			banner={<DriverTrainingBanner />}
			dock={renderDock()}
			refreshControl={<RefreshControl refreshing={isRefreshing} onRefresh={refreshPage} />}
		>
			{renderBody()}
			{
				Boolean(returnStep) && (
					<Sheet
						isVisible={isReturnSheetOpen}
						onClose={closeReturnSheet}
						title={returnStep.label}
						footer={(
							<PrimaryButton
								label={isOnline ? ACTION_LABEL.returnedToWarehouse : 'Connect to confirm return'}
								onPress={confirmReturnFromSheet}
								loading={isConfirmingReturnedToWarehouse}
								disabled={!isOnline || !returnStep.canConfirm}
							/>
						)}
					>
						<Text style={styles.sheetText}>{returnStep.description}</Text>
					</Sheet>
				)
			}
		</Screen>
	);
};

const createStyles = ({ ui: { colors, space, type } }) => ({
	runCard: {
		flexDirection: 'row',
		alignItems: 'center',
		gap: space.md,
		padding: 14,
	},
	runCardText: {
		flex: 1,
		gap: 2,
	},
	runCardTitle: {
		...type.sectionTitle,
		color: colors.text,
	},
	runCardSummary: {
		...type.secondary,
		color: colors.onSuccessCard,
	},
	runCardPending: {
		...type.secondary,
		color: colors.textSecondary,
	},
	steps: {
		gap: space.sm,
	},
	sheetText: {
		...type.body,
		color: colors.text,
	},
});

export default EndOfDayView;
